import { TranslateService, TranslateModule } from '@ngx-translate/core';
import { ToastService } from '../services/toast.service';
import { SpinnerService } from '../spinner/spinner.service';
import { catchError, map, tap } from 'rxjs/operators';
import { SetPasswordDTO, UserAccountClient } from '../models/models.swagger.generated';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, Validators, ReactiveFormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { interval, of, Subscription } from 'rxjs';
import { loginRoute } from 'src/app/core/routing/route-names';
import { ErrorType } from 'src/app/core/routing/error-types';
import { AppErrorMessagesService } from '../services/app-error-messages.service';
import { CustomValidatorsFactory } from '../shared/validators/custom-validators-factory';
import { AppErrorMessagesComponent } from '../error-messages/app-error-messages.component';
import { PasswordInputComponent } from '../shared/password-input/password-input.component';

import { TitleBarComponent } from '../shared/title-bar/title-bar.component';
import { SetPasswordFormGroup, SetPasswordFormType } from './set-password-form.type';

@Component({
    selector: 'app-set-password',
    templateUrl: './set-password.component.html',
    styleUrls: ['./set-password.component.scss'],
    imports: [TitleBarComponent, ReactiveFormsModule, PasswordInputComponent, AppErrorMessagesComponent, RouterLink, TranslateModule]
})
export class SetPasswordComponent implements OnInit, OnDestroy {

  public setPasswordForm: SetPasswordFormGroup;
  public passwordSetSuccess = false;
  public loginRoute = loginRoute;
  public autoRedirectMessage: string;
  private autoRedirectTimeInSeconds = 5;
  private userId: string;
  private verificationToken: string;
  private forgotPasswordProcess: boolean;
  private timeOutSubscription: Subscription;

  public get formFields() {
    return this.setPasswordForm.controls;
  }

  public get areParametersValid(): boolean {
    return !!((this.userId) && (this.verificationToken));
  }

  constructor(
    private formBuilder: FormBuilder,
    private userAccountClient: UserAccountClient,
    private route: ActivatedRoute,
    private spinnerService: SpinnerService,
    private toastService: ToastService,
    private errorMessageService: AppErrorMessagesService,
    private translateService: TranslateService,
    private router: Router
  ) { }

  public ngOnInit(): void {
    this.initializeForm();
    this.route.queryParams.subscribe(params => {
      this.userId = params.userId;
      this.verificationToken = params.code;
      this.forgotPasswordProcess = !!params.fp;
    });
  }

  public ngOnDestroy(): void {
    if (this.timeOutSubscription) {
      this.timeOutSubscription.unsubscribe();
    }
  }

  public setPassword(): void {
    if (this.setPasswordForm.valid) {
      const formData = this.setPasswordForm.value;
      const setPasswordDTO: SetPasswordDTO = {
        newPassword: formData.password,
        userId: this.userId,
        verificationToken: this.verificationToken,
        forgotPasswordProcess: this.forgotPasswordProcess
      } as SetPasswordDTO;

      this.spinnerService.show();
      this.userAccountClient.setPassword(setPasswordDTO).pipe(
        tap(
          () => {
            this.spinnerService.hide();
            this.passwordSetSuccess = true;
            this.redirectToLogin();
          }),
        catchError((error: any) => {
          this.spinnerService.hide();
          this.handleError(error);
          return of(error);
        })).subscribe();
    }
  }

  private redirectToLogin(): void {
    this.timeOutSubscription = interval(1000).pipe(
      map((x) => {
        this.autoRedirectTimeInSeconds--;
        if (this.autoRedirectTimeInSeconds > 0) {
          this.autoRedirectMessage = this.translateService.instant('i18n.base.set-password.redirect-message',
            { params: this.autoRedirectTimeInSeconds });
        }
        else {
          this.router.navigate([this.loginRoute]);
        }
      })
    ).subscribe();
  }

  private handleError(error: any): void {
    const apiErrorCode = AppErrorMessagesService.getApiErrorCode(error);
    let i18nErrorMessage = 'i18n.errors.server-error.message';
    const i18nErrorTitle = 'i18n.errors.server-error.title';
    if (apiErrorCode) {
      i18nErrorMessage = 'i18n.base.set-password.errors.' + apiErrorCode;
    }
    this.toastService.showError(i18nErrorTitle, i18nErrorMessage,
      null, ErrorType.serverError);
  }

  private initializeForm(): void {
    this.setPasswordForm = this.formBuilder.group<SetPasswordFormType>({
      password: this.formBuilder.control(null, [Validators.required, CustomValidatorsFactory.passwordValidation()]),
      passwordConfirm: this.formBuilder.control(null, [Validators.required, CustomValidatorsFactory.passwordValidation()])
    },
      {
        validators: [CustomValidatorsFactory.passwordMatchValidation()]
      });
  }
}

